import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { Resource } from "@opentelemetry/resources";
import {
  BatchSpanProcessor,
  BatchSpanProcessorBrowserConfig,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
  WebTracerProvider,
} from "@opentelemetry/sdk-trace-web";
import { ClientJS } from "clientjs";

import { DEFAULT_SCHEDULED_DELAY_MILLIS } from "./constants";
import { ProviderConfig, ProviderType } from "./types";
import { getSampleRateRatio } from "./utils";
import { log } from "./utils/logger";

class ProviderClass {
  provider: ProviderType;
  client: ClientJS;
  static entryPage: Record<string, string> = {};
  static resourceAttrs: Record<string, any> = {};

  constructor(config: ProviderConfig, ipAddress?: string) {
    this.client = new ClientJS();
    if (!ProviderClass.entryPage.url) {
      ProviderClass.entryPage = this._getEntryPageAttrs();
    }
    if (!ProviderClass.resourceAttrs["user_agent.original"]) {
      const { serviceName, deploymentEnvironment, appVersion, ...restResourceAttributes } =
        config.resourceAttributes || {};

      log(serviceName, deploymentEnvironment, appVersion, restResourceAttributes);

      ProviderClass.resourceAttrs = this._getResourceAttrs(restResourceAttributes, ipAddress);
    }
    this.provider = this._init(config);
    this.provider.register();
  }

  private _getResourceAttrs(resourceAttributes: any, ipAddress?: string) {
    const nav = navigator as any;
    const win = window as any;
    const screenSize = win.screen?.width
      ? win.screen.width < 768
        ? "small"
        : win.screen.width < 1024
          ? "medium"
          : "large"
      : "unknown";
    const attrs = {
      ...resourceAttributes,
      "user_agent.original": navigator.userAgent,
      "browser.mobile": nav.userAgentData?.mobile ?? this.client.isMobile(),
      "browser.touch_screen_enabled": "maxTouchPoints" in navigator && navigator.maxTouchPoints > 0,
      "browser.language": navigator.language,
      "browser.name": nav.userAgentData?.brands?.[0]?.brand || this.client.getBrowser(),
      "browser.version":
        nav.userAgentData?.brands?.[0]?.version || this.client.getBrowserMajorVersion(),
      "browser.platform": nav.userAgentData?.platform || this.client.getOS(),
      "browser.brands": nav.userAgentData?.brands
        ? JSON.stringify(nav.userAgentData.brands)
        : undefined,
      "device.type": this.client.getDeviceType(),
      "network.effectiveType": (navigator as any).connection?.effectiveType,
      "screen.width": win.screen?.width,
      "screen.height": win.screen?.height,
      "screen.size": screenSize,
      "browser.width": win.innerWidth,
      "browser.height": win.innerHeight,
      ...(ipAddress ? { "client.ip": ipAddress } : {}),
      ...ProviderClass.entryPage,
    };
    // Remove attributes that are null, undefined, or empty string
    return Object.fromEntries(
      Object.entries(attrs).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    );
  }

  private _getEntryPageAttrs() {
    return {
      "entry_page.url": window.location.href,
      "entry_page.path": window.location.pathname,
      "entry_page.search": window.location.search,
      "entry_page.hash": window.location.hash,
      "entry_page.hostname": window.location.hostname,
      "entry_page.referrer": document.referrer,
    };
  }

  private _init(config: ProviderConfig) {
    const { sampleRate, resourceAttributes } = config;
    const { serviceName, deploymentEnvironment, appVersion, ...restResourceAttributes } =
      resourceAttributes || {};

    const exporter = this._getExporter(config);
    const ratio = getSampleRateRatio(sampleRate);

    return new WebTracerProvider({
      resource: new Resource({
        ...ProviderClass.resourceAttrs,
        "service.name": serviceName,
        "deployment.environment": deploymentEnvironment,
        "app.version": appVersion,
        ...restResourceAttributes,
      }),
      sampler: new ParentBasedSampler({
        root: new TraceIdRatioBasedSampler(ratio),
      }),
      spanProcessors: [
        new BatchSpanProcessor(exporter, {
          disableAutoFlushOnDocumentHide: true, // to track all traces in one request. disabled auto flush
          scheduledDelayMillis: DEFAULT_SCHEDULED_DELAY_MILLIS, // to avoid auto flush. override this time from 5 s to default.
        } as BatchSpanProcessorBrowserConfig),
      ],
    });
  }

  private _getOrigin() {
    return window.location.origin;
  }

  private _getClientId() {
    return this.client.getFingerprint().toString();
  }

  private _getExporter(config: ProviderConfig) {
    const { clientToken, ...restHeaders } = config.headers;
    return new OTLPTraceExporter({
      url: config.endpoint,
      headers: {
        "Content-Type": "application/json",
        "X-LAST9-API-TOKEN": `Bearer ${clientToken}`,
        "X-LAST9-ORIGIN": this._getOrigin(),
        "Client-ID": this._getClientId(),
        ...restHeaders,
      },
    });
  }
}

export const initProvider = async (config: ProviderConfig) => {
  let ipAddress: string | undefined = undefined;
  try {
    const resp = await fetch("https://api.ipify.org?format=json");
    const data = await resp.json();
    ipAddress = data.ip;
  } catch (e) {
    log(e);
    // Ignore IP fetch errors, proceed without IP
  }
  const providerInstance = new ProviderClass(config, ipAddress);
  return providerInstance.provider;
};
